import { dbStorage } from './indexedDBStorage'
import { readLedgerSafePostgresPrimaryRecentScope } from './ledgerSafePostgresPrimaryReadBridge'
import type { AnchoredRow } from '../types/anchoredRowTypes'
import type {
  CreateLedgerSafeEditorialEventInput,
  LedgerSafeEditorialEvent,
  LedgerSafeEditorialOperationKind,
  LedgerSafeEditorialRowSnapshot,
  LedgerSafeToolCallProjection
} from '../types/ledgerSafeEditorialEvents'
import { editorIdentityToContributor, ensureActiveEditorIdentity } from './editorIdentityStore'

const DEFAULT_ACTOR_ID = 'local-user'

const nowIso = (): string => new Date().toISOString()

const generateId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `ledger-safe-edit-${Date.now()}-${Math.random().toString(16).slice(2)}`
}

const cloneUnitMappings = (row: AnchoredRow): LedgerSafeEditorialRowSnapshot['unitMappings'] =>
  row.unitMappings.map(mapping => ({ ...mapping }))

const cloneEditorialMetadata = (row: AnchoredRow): AnchoredRow['metadata'] => {
  const metadata: AnchoredRow['metadata'] = {}
  if (row.metadata?.rowTitle) {
    metadata.rowTitle = JSON.parse(JSON.stringify(row.metadata.rowTitle))
  }
  if (row.metadata?.scene) {
    metadata.scene = JSON.parse(JSON.stringify(row.metadata.scene))
  }
  return Object.keys(metadata).length ? metadata : undefined
}

export const buildLedgerSafeEditorialRowSnapshots = (
  rows: AnchoredRow[],
  rowIds: string[]
): LedgerSafeEditorialRowSnapshot[] => {
  const rowIdSet = new Set(rowIds)
  return rows
    .filter(row => rowIdSet.has(row.id))
    .map(row => ({
      id: row.id,
      chapterId: row.chapterId,
      labelPrefix: row.labelPrefix,
      labelNumber: row.labelNumber,
      position: row.position,
      status: row.status,
      unitMappings: cloneUnitMappings(row),
      metadata: cloneEditorialMetadata(row)
    }))
    .sort((left, right) => {
      if (left.position !== right.position) {
        return left.position - right.position
      }
      return left.labelNumber - right.labelNumber
    })
}

const asString = (value: unknown): string => String(value || '').trim()

const asStringArray = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.map(item => asString(item)).filter(Boolean)
    : []

const asInteger = (value: unknown): number | null => {
  const numeric = Number(value)
  return Number.isInteger(numeric) ? numeric : null
}

export const buildLedgerSafeToolCallProjection = (
  kind: LedgerSafeEditorialOperationKind,
  params: Record<string, unknown>
): LedgerSafeToolCallProjection | null => {
  switch (kind) {
    case 'boundary_shift': {
      const witnessId = asString(params.witnessId)
      const leftRowId = asString(params.leftRowId)
      const deltaWords = asInteger(params.deltaWords)
      if (!witnessId || !leftRowId || deltaWords === null) {
        return null
      }
      return {
        toolName: 'preview_boundary_shift',
        args: { witnessId, leftRowId, deltaWords }
      }
    }
    case 'split_unit': {
      const witnessId = asString(params.witnessId)
      const rowId = asString(params.rowId)
      const afterWordIndex = asInteger(params.afterWordIndex)
      if (!witnessId || !rowId || afterWordIndex === null || afterWordIndex < 1) {
        return null
      }
      return {
        toolName: 'preview_split_unit',
        args: { witnessId, rowId, afterWordIndex }
      }
    }
    case 'merge_units': {
      const witnessId = asString(params.witnessId)
      const rowIds = asStringArray(params.rowIds)
      if (!witnessId || rowIds.length < 2) {
        return null
      }
      return {
        toolName: 'preview_merge_units',
        args: { witnessId, rowIds }
      }
    }
    case 'shift_range': {
      const witnessId = asString(params.witnessId)
      const rowIds = asStringArray(params.rowIds)
      const direction = params.direction === 'up' ? 'up' : params.direction === 'down' ? 'down' : null
      if (!witnessId || rowIds.length < 1 || !direction) {
        return null
      }
      return {
        toolName: 'preview_shift_range',
        args: { witnessId, rowIds, direction }
      }
    }
    default:
      return null
  }
}

export const createLedgerSafeEditorialEvent = (
  input: CreateLedgerSafeEditorialEventInput
): LedgerSafeEditorialEvent => ({
  id: generateId(),
  chapterId: input.chapterId,
  createdAt: nowIso(),
  actor: input.actor || { kind: 'human', id: DEFAULT_ACTOR_ID, displayName: 'Local editor' },
  source: input.source,
  summary: input.summary,
  operation: input.operation,
  toolCallProjection: buildLedgerSafeToolCallProjection(input.operation.kind, input.operation.params),
  affectedWitnessIds: Array.from(new Set(input.affectedWitnessIds)),
  affectedRowIds: Array.from(new Set(input.affectedRowIds)),
  beforeRows: input.beforeRows,
  afterRows: input.afterRows,
  uiContext: input.uiContext,
  metadata: input.metadata
})

export const recordLedgerSafeEditorialEvent = async (
  input: CreateLedgerSafeEditorialEventInput
): Promise<LedgerSafeEditorialEvent> => {
  const activeEditor = input.actor ? null : await ensureActiveEditorIdentity()
  const event = createLedgerSafeEditorialEvent({
    ...input,
    actor: input.actor || (activeEditor
      ? editorIdentityToContributor(activeEditor, 'editor')
      : { kind: 'human', id: DEFAULT_ACTOR_ID, displayName: 'Local editor' }),
  })
  await dbStorage.put('ledger_safe_edit_events', event)
  return event
}

export const listLedgerSafeEditorialEventsForChapter = async (
  chapterId: string,
  options: { limit?: number } = {},
): Promise<LedgerSafeEditorialEvent[]> => {
  const limit = options.limit && options.limit > 0
    ? Math.floor(options.limit)
    : null
  const recentPrimaryEvents = limit
    ? await readLedgerSafePostgresPrimaryRecentScope({
        databaseName: 'ScholarlyWorkflowDB',
        storeName: 'ledger_safe_edit_events',
        scope: { kind: 'chapter', id: chapterId },
        limit,
        maxBytes: 4_000_000,
      })
    : null
  const events = recentPrimaryEvents === null
    ? await dbStorage.getAllByIndex('ledger_safe_edit_events', 'chapterId', chapterId)
    : recentPrimaryEvents as LedgerSafeEditorialEvent[]
  const ordered = [...events].sort((left, right) => right.createdAt.localeCompare(left.createdAt))
  return limit ? ordered.slice(0, limit) : ordered
}
